import { Link } from "react-router-dom";
import { ArrowLeft, Download, CheckCircle2, Clock, AlertCircle } from "lucide-react";

const PROGRESS_ROWS = [
  { area: "Prohibitions (Article 5)", status: "live", note: "In force since 2 Feb 2025. Inventory and red-line list expected at board level." },
  { area: "GPAI obligations", status: "live", note: "In force since 2 Aug 2025. Foundation-model vendors expected to supply technical documentation, training summaries, copyright policy." },
  { area: "Transparency duties", status: "live", note: "Disclosure for AI-generated content and chatbot interactions. Mechanisms still maturing." },
  { area: "National competent authorities", status: "live", note: "Designated. Ireland's market-surveillance posture coordinated with sectoral regulators." },
  { area: "AI Office (EU-level)", status: "live", note: "Operational. GPAI supervision, systemic-risk incident channel, Code of Practice." },
  { area: "High-risk AI (Annex III)", status: "delayed", note: "Original deadline 2 Aug 2026. Digital Omnibus proposes 2 Dec 2027. Trilogue in progress." },
  { area: "High-risk AI (Annex I safety components)", status: "delayed", note: "Original deadline 2 Aug 2027. Digital Omnibus proposes 2 Aug 2028." },
  { area: "Ireland's EU Council Presidency", status: "upcoming", note: "1 July – 31 December 2026. Dublin International AI Summit on 14 Oct 2026 at the RDS." },
];

const STATUS_STYLES = {
  live: { label: "Live", icon: CheckCircle2, klass: "text-[#15803d] border-[#15803d]" },
  delayed: { label: "Under review", icon: AlertCircle, klass: "text-[var(--eai-accent)] border-[var(--eai-accent)]" },
  upcoming: { label: "Upcoming", icon: Clock, klass: "text-[var(--eai-ink)] border-[var(--eai-ink)]" },
};

const SECTIONS = [
  {
    h: "01 — A Distinguishing Position",
    p: [
      "Ireland enters 2026 with two distinguishing features. It is among the most AI-intensive economies in the European Union — by foreign direct investment, by data-centre footprint, by share of GVA tied to the foundation-model and platform sector. And from 1 July, it holds the rotating presidency of the Council of the European Union.",
      "The conjunction is consequential. Most presidencies inherit a digital agenda. This one will shape the operational definition of European AI ethics at the precise moment the AI Act's heaviest obligations are being renegotiated under the Digital Omnibus.",
    ],
  },
  {
    h: "02 — Ethics, Innovation, and the Strategic Brief",
    p: [
      "The argument of this brief is that ethics and competitiveness are not in tension in the European framing — they are co-determined. The Digital Omnibus has shown that a regulatory regime built without operational latitude does not survive contact with industry. But the inverse is also true: an innovation agenda that does not anchor itself in dignity, transparency, and accountability does not survive contact with the public.",
      "Ireland's positioning depends on holding both sides. Domestic policy work, presidency conclusions, and the Dublin AI Summit are the three principal instruments available in the coming twelve months.",
    ],
  },
  {
    h: "03 — Three Pillars of the Presidency",
    p: [
      "The publicly stated priorities of the Irish presidency are competitiveness, security and defence, and enlargement, alongside negotiations on the Multiannual Financial Framework for 2028–2034. AI is not formally a fourth pillar — but it cuts across all three. Competitiveness will be the framing of the Dublin AI Summit (14 October 2026 at the Royal Dublin Society, launching European AI Innovation Month under the theme “Harnessing AI to Revolutionise Europe's Competitiveness”). Security and defence policy will increasingly intersect with frontier-model governance. Enlargement and the MFF set the resource envelope for European AI infrastructure.",
    ],
  },
  {
    h: "04 — What This Means for Practitioners",
    p: [
      "For Irish-headquartered organisations, the six months of presidency are an exceptional convening opportunity — and a moment when domestic policy work travels further than usual. For multinational operators with EU exposure, Ireland's posture matters not because it is unique but because, in this window, it is influential.",
      "The recommendation of this brief is straightforward. Maintain compliance discipline against the obligations that are already live. Use the presidency window to engage on operational definitions — what counts as adequate transparency, what counts as sufficient post-market monitoring, what counts as responsible foundation-model release. The runway extended by the Digital Omnibus is not time off; it is time to shape the standard.",
    ],
  },
];

export default function IrelandReadiness() {
  return (
    <article data-testid="ireland-readiness-page">
      {/* Header */}
      <header className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <Link to="/intelligence" className="inline-flex items-center gap-2 text-xs font-mono-eai uppercase tracking-[0.18em] text-[var(--eai-ink-2)] hover:text-[var(--eai-ink)]" data-testid="back-to-intelligence-link">
            <ArrowLeft size={14} /> Back to Intelligence
          </Link>
          <p className="overline mt-8">Strategic Brief · September 2025</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[64px] font-bold tracking-tight leading-[1.02]">
            Ireland&rsquo;s AI Readiness: A Strategic Brief on Ethics, Innovation &amp; Global Positioning
          </h1>
          <p className="mt-8 text-xl text-[var(--eai-ink-2)] leading-relaxed max-w-3xl">
            Written ahead of Ireland&rsquo;s 2026 Council Presidency. A practitioner-and-policy view of where Ireland stands on the EU AI Act — what is live, what is delayed, and what the next twelve months will require of regulators, organisations, and the country itself.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-8 text-xs font-mono-eai uppercase tracking-[0.18em] text-[var(--eai-ink-2)]">
            <span>Matthew Kilkenny — Founder</span>
            <span>·</span>
            <span>12 min read</span>
            <span>·</span>
            <span>Updated September 2025</span>
          </div>
        </div>
      </header>

      {/* Executive Summary */}
      <section className="border-b border-[var(--eai-border)] bg-[var(--eai-bg-2)]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3">
            <p className="overline">Executive summary</p>
          </div>
          <div className="lg:col-span-9 space-y-5 text-lg text-[var(--eai-ink)] leading-relaxed">
            <p>
              The EU AI Act has moved from legislation to operating regime. The most consequential obligations — prohibitions, general-purpose AI duties, and transparency — are already live and being enforced. The high-risk regime, the heaviest compliance lift, is being recalibrated under the November 2025 Digital Omnibus, with trilogue under way at the time of writing.
            </p>
            <p>
              Ireland enters this moment with disproportionate influence. The country is among the EU&rsquo;s most AI-intensive economies and, from July 2026, will hold the rotating presidency of the Council. The Dublin International AI Summit at the Royal Dublin Society on 14 October 2026 will officially launch European AI Innovation Month under the theme &ldquo;Harnessing AI to Revolutionise Europe&rsquo;s Competitiveness.&rdquo;
            </p>
            <p>
              This brief argues that ethics and competitiveness are co-determined, not opposed, in the European framing — and that Ireland&rsquo;s national posture in the coming twelve months will materially shape the operational definition of responsible AI across the bloc.
            </p>
          </div>
        </div>
      </section>

      {/* Progress at a Glance */}
      <section className="border-b border-[var(--eai-border)]">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-3">
              <p className="overline">Table 01</p>
            </div>
            <div className="lg:col-span-9">
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight leading-tight">Progress at a Glance</h2>
              <p className="mt-3 text-[var(--eai-ink-2)]">Where each major component of the EU AI Act stands as of the publication date, with Irish-relevant context.</p>
            </div>
          </div>

          <div className="border border-[var(--eai-border)]" data-testid="progress-at-a-glance-table">
            <div className="hidden md:grid grid-cols-12 gap-0 bg-[var(--eai-bg-2)] border-b border-[var(--eai-border)] font-mono-eai text-[11px] uppercase tracking-[0.18em] text-[var(--eai-ink-2)]">
              <div className="col-span-5 px-5 py-4 border-r border-[var(--eai-border)]">Component</div>
              <div className="col-span-2 px-5 py-4 border-r border-[var(--eai-border)]">Status</div>
              <div className="col-span-5 px-5 py-4">Note</div>
            </div>
            {PROGRESS_ROWS.map((row, i) => {
              const style = STATUS_STYLES[row.status];
              const Icon = style.icon;
              return (
                <div
                  key={row.area}
                  className={`grid grid-cols-1 md:grid-cols-12 ${i < PROGRESS_ROWS.length - 1 ? "border-b border-[var(--eai-border)]" : ""}`}
                  data-testid={`progress-row-${i}`}
                >
                  <div className="md:col-span-5 px-5 py-5 md:border-r border-[var(--eai-border)] font-display text-lg leading-snug">{row.area}</div>
                  <div className="md:col-span-2 px-5 py-5 md:border-r border-[var(--eai-border)] flex items-center gap-2">
                    <Icon size={16} strokeWidth={1.8} className={style.klass.split(" ")[0]} />
                    <span className={`font-mono-eai text-[11px] uppercase tracking-[0.18em] ${style.klass.split(" ")[0]}`}>{style.label}</span>
                  </div>
                  <div className="md:col-span-5 px-5 py-5 text-[var(--eai-ink-2)] text-sm leading-relaxed">{row.note}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Body */}
      <section>
        <div className="max-w-[820px] mx-auto px-6 lg:px-12 py-16 lg:py-24 space-y-14">
          {SECTIONS.map((s) => (
            <div key={s.h}>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">{s.h}</h2>
              <div className="mt-5 space-y-5">
                {s.p.map((para, i) => (
                  <p key={`${s.h}-${i}`} className="font-display text-xl leading-[1.6] text-[var(--eai-ink)]">{para}</p>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-16 pt-10 border-t border-[var(--eai-border)]">
            <p className="overline">Authorship &amp; provenance</p>
            <p className="mt-4 text-[var(--eai-ink-2)] leading-relaxed">
              Authored by <span className="text-[var(--eai-ink)] font-medium">Matthew Kilkenny</span>, founder of Ethical AI Now. Drafted from publicly available material, presidency communications, and the November 2025 Digital Omnibus package. A printable PDF and a sourced bibliography are available on request.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="eai-btn" data-testid="ireland-request-pdf-cta">
                Request the PDF <Download size={16} />
              </Link>
              <Link to="/intelligence" className="eai-link reveal-underline text-sm font-mono-eai uppercase tracking-[0.18em]">
                More from the Intelligence Hub →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
